import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import * as moment from 'moment';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AuthenticationService } from './authentication.service';
import { AssessmentService } from './assessment.service';
import { AppEnum } from '@enums/AppEnum';
import {
  Regiment,
  TrainingRecommendation,
  TrainingType,
  AssessmentLevel,
  SubscoreCategory,
  SubscoreFlag,
  AssessmentReport,
  TestProfile
} from '@models/regiment.model';

/**
 * Regiment Service
 * 
 * Responsible for generating, assigning, and tracking a user's weekly training regiment
 * based on assessment performance. Designed to coexist with the existing recommended
 * training / home service system.
 * 
 * Data is stored in a subcollection under each user document:
 * users/{userId}/regiments/{regimentId}
 */
@Injectable({
  providedIn: 'root'
})
export class RegimentService {

  /** Current week's regiment */
  private currentRegiment: Regiment | null = null;

  /** All loaded regiments for the user */
  private regiments: Regiment[] = [];

  /** Current user */
  private user: any;

  /** Whether regiments have been loaded */
  private regimentsLoaded = false;

  /** Subject that emits when regiments are loaded */
  public regimentsLoaded$ = new Subject<void>();

  /** BehaviorSubject for the current week's regiment */
  public currentRegiment$ = new BehaviorSubject<Regiment | null>(null);

  /** Currently selected test profile (null = use real data) */
  private activeTestProfile: TestProfile | null = null;

  /**
   * Base recommended training (baseline sessions per week)
   */
  private readonly BASE_TRAINING: Record<TrainingType, { sessions: number; displayName: string; iconPath: string }> = {
    braingame: { sessions: 5, displayName: 'Brain Game', iconPath: 'assets/apps/braingame/icon.png' },
    yahootie: { sessions: 3, displayName: 'Yahootie', iconPath: 'assets/apps/yahootie/icon.png' },
    clubhouse: { sessions: 1, displayName: 'Clubhouse', iconPath: 'assets/apps/clubhouse/icon.png' },
    happyplace: { sessions: 5, displayName: 'Happy Place', iconPath: 'assets/apps/happyplace/icon.png' },
    outsidetraining: { sessions: 5, displayName: 'Outside Training', iconPath: 'assets/apps/outsidetraining/icon.png' }
  };

  /**
   * Mapping from AppEnum to TrainingType for regiment completion tracking.
   * This enables automatic session completion when apps finish.
   */
  private readonly APP_TO_TRAINING_TYPE: Partial<Record<AppEnum, TrainingType>> = {
    [AppEnum.BrainGame]: 'braingame',
    [AppEnum.Yahootie]: 'yahootie',
    [AppEnum.HappyPlace]: 'happyplace',
    [AppEnum.Clubhouse]: 'clubhouse',
    [AppEnum.OutsideTraining]: 'outsidetraining'
  };

  /**
   * Score thresholds for determining assessment level
   */
  private readonly SCORE_THRESHOLDS = {
    HIGH: 7,
    LOW: 4
  };

  /**
   * Test profiles for simulating different user scenarios
   */
  public readonly TEST_PROFILES: TestProfile[] = [
    {
      id: 'real',
      name: 'Use Real Data',
      description: 'Use actual assessment data from the database',
      scores: { focus: 5, speed: 5, memory: 5, strategy: 5, wisdom: 5 }
    },
    {
      id: 'high-performer',
      name: 'High Performer',
      description: 'All scores above average (7-9)',
      scores: { focus: 8, speed: 9, memory: 7, strategy: 8, wisdom: 7 }
    },
    {
      id: 'low-performer',
      name: 'Low Performer',
      description: 'All scores below average (2-4)',
      scores: { focus: 3, speed: 2, memory: 4, strategy: 3, wisdom: 4 }
    },
    {
      id: 'average',
      name: 'Average Performer',
      description: 'All scores around average (5-6)',
      scores: { focus: 5, speed: 6, memory: 5, strategy: 6, wisdom: 5 }
    },
    {
      id: 'focus-deficit',
      name: 'Focus & Speed Deficit',
      description: 'Low Focus and Speed, other areas strong',
      scores: { focus: 2, speed: 3, memory: 7, strategy: 8, wisdom: 6 }
    },
    {
      id: 'memory-deficit',
      name: 'Memory & Strategy Deficit',
      description: 'Low Memory and Strategy, other areas strong',
      scores: { focus: 7, speed: 8, memory: 3, strategy: 2, wisdom: 6 }
    },
    {
      id: 'mixed',
      name: 'Mixed Performance',
      description: 'Some high, some low scores',
      scores: { focus: 9, speed: 3, memory: 7, strategy: 4, wisdom: 5 }
    }
  ];

  constructor(
    private afs: AngularFirestore,
    private authService: AuthenticationService,
    private assessmentService: AssessmentService
  ) {
    this.initializeService();
  }

  /**
   * Initialize the service by subscribing to user changes
   */
  private initializeService(): void {
    this.authService.userSubject.subscribe(async user => {
      if (!user) { return; }
      this.user = user;
      await this.loadUserRegiments(user.id);
      
      // After loading, ensure current week has a regiment
      await this.ensureCurrentWeekRegiment();
    });
  }

  /**
   * Ensure a regiment exists for the current week, generate if needed
   */
  private async ensureCurrentWeekRegiment(): Promise<void> {
    if (!this.user) { return; }

    const weekStart = this.getWeekStartDate();
    let regiment = this.findRegimentForWeek(weekStart);

    if (!regiment) {
      console.log('RegimentService: No regiment for current week, generating...');
      regiment = await this.generateAndSaveRegiment();
    }

    this.currentRegiment = regiment;
    this.currentRegiment$.next(regiment);
  }

  // ============================================
  // PUBLIC API
  // ============================================

  /**
   * Get the current week's regiment for the user.
   * Will wait for user to be available if not yet loaded.
   */
  async getCurrentWeekRegiment(): Promise<Regiment | null> {
    // Wait for user to be available
    await this.ensureUserLoaded();
    
    if (!this.user) {
      console.warn('RegimentService: No user available after waiting');
      return null;
    }

    await this.ensureRegimentsLoaded();

    const weekStart = this.getWeekStartDate();
    let regiment = this.findRegimentForWeek(weekStart);

    if (!regiment) {
      console.log('RegimentService: Generating regiment for current week');
      regiment = await this.generateAndSaveRegiment();
    }

    this.currentRegiment = regiment;
    this.currentRegiment$.next(regiment);
    return regiment;
  }

  /**
   * Wait for user to be loaded
   */
  private async ensureUserLoaded(): Promise<void> {
    if (this.user) { return; }

    const maxWaitTime = 5000;
    const checkInterval = 100;
    let waited = 0;

    while (!this.user && waited < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waited += checkInterval;
    }
  }

  /**
   * Get all historical regiments for the user
   */
  async getAllRegiments(): Promise<Regiment[]> {
    await this.ensureRegimentsLoaded();
    return this.regiments;
  }

  /**
   * Force regenerate the current week's regiment
   */
  async regenerateCurrentRegiment(): Promise<Regiment | null> {
    if (!this.user) {
      console.warn('RegimentService: No user available');
      return null;
    }

    return await this.generateAndSaveRegiment();
  }

  /**
   * Set the active test profile for simulating different user scenarios
   */
  setTestProfile(profileId: string): void {
    if (profileId === 'real') {
      this.activeTestProfile = null;
    } else {
      this.activeTestProfile = this.TEST_PROFILES.find(p => p.id === profileId) || null;
    }
  }

  /**
   * Get the currently active test profile
   */
  getActiveTestProfile(): TestProfile | null {
    return this.activeTestProfile;
  }

  /**
   * Toggle session completion status (for manual UI interaction)
   */
  async toggleSessionComplete(trainingType: TrainingType, sessionIndex: number): Promise<void> {
    if (!this.currentRegiment) { return; }

    const recommendation = this.currentRegiment.trainingRecommendations.find(
      r => r.trainingType === trainingType
    );

    if (recommendation && sessionIndex < recommendation.sessionsCompleted.length) {
      const nextValue = !recommendation.sessionsCompleted[sessionIndex];

      // Enforce: only allow ONE session completion per calendar day PER app/trainingType.
      // Always allow unchecking.
      if (nextValue) {
        const today = moment().format('YYYY-MM-DD');
        const lastByType = this.currentRegiment.lastSessionCompletedDayByTrainingType || {};
        if (lastByType[trainingType] === today) {
          console.log('RegimentService: Session completion already recorded today for this app; blocking additional completion.');
          return;
        }
        this.currentRegiment.lastSessionCompletedDayByTrainingType = {
          ...lastByType,
          [trainingType]: today
        };
      }

      recommendation.sessionsCompleted[sessionIndex] = nextValue;
      await this.saveRegiment(this.currentRegiment);
      this.currentRegiment$.next(this.currentRegiment);
    }
  }

  /**
   * Mark the next incomplete session as complete for a given app.
   * Called automatically when an app completes (before end-screen appears).
   * This is a one-way operation (only marks complete, never unchecks).
   *
   * This method maintains compatibility with HomeService and OverallSessionService
   * by working as a parallel tracking system - apps should continue to call
   * OverallSessionService.saveOverallSessionApp() in addition to this method.
   *
   * @param appName The app that was completed (from AppEnum)
   * @returns Promise<boolean> True if a session was marked complete, false otherwise
   */
  async markSessionComplete(appName: AppEnum): Promise<boolean> {
    // Map AppEnum to TrainingType
    const trainingType = this.APP_TO_TRAINING_TYPE[appName];
    if (!trainingType) {
      console.log('RegimentService: No training type mapping for app:', appName);
      return false;
    }

    // Ensure regiment is loaded
    if (!this.currentRegiment) {
      await this.ensureUserLoaded();
      await this.ensureRegimentsLoaded();
      if (!this.currentRegiment) {
        await this.getCurrentWeekRegiment();
      }
    }

    if (!this.currentRegiment) {
      console.warn('RegimentService: No regiment available to mark session complete');
      return false;
    }

    // Enforce: only allow ONE session completion per calendar day PER app/trainingType.
    const today = moment().format('YYYY-MM-DD');
    const lastByType = this.currentRegiment.lastSessionCompletedDayByTrainingType || {};
    if (lastByType[trainingType] === today) {
      console.log('RegimentService: Session completion already recorded today for this app; skipping auto-complete.');
      return false;
    }

    // Find the training recommendation for this type
    const recommendation = this.currentRegiment.trainingRecommendations.find(
      r => r.trainingType === trainingType
    );

    if (!recommendation) {
      console.warn('RegimentService: No recommendation found for training type:', trainingType);
      return false;
    }

    // Find the first incomplete session
    const nextIncompleteIndex = recommendation.sessionsCompleted.findIndex(completed => !completed);

    if (nextIncompleteIndex === -1) {
      // All sessions already complete
      console.log('RegimentService: All sessions already complete for:', trainingType);
      return false;
    }

    // Mark the session as complete
    recommendation.sessionsCompleted[nextIncompleteIndex] = true;
    this.currentRegiment.lastSessionCompletedDayByTrainingType = {
      ...lastByType,
      [trainingType]: today
    };

    console.log(`RegimentService: Marked session ${nextIncompleteIndex + 1}/${recommendation.sessionsCompleted.length} complete for ${trainingType}`);

    // Save the updated regiment
    await this.saveRegiment(this.currentRegiment);

    // Emit the updated regiment so all subscribers get the update
    this.currentRegiment$.next(this.currentRegiment);

    return true;
  }

  /**
   * Get TrainingType from AppEnum
   * Utility method for components that need to map app names to training types
   */
  getTrainingTypeForApp(appName: AppEnum): TrainingType | null {
    return this.APP_TO_TRAINING_TYPE[appName] || null;
  }

  // ============================================
  // REGIMENT GENERATION LOGIC
  // ============================================

  /**
   * Generate a new regiment based on assessment data (or test profile)
   */
  private async generateRegiment(): Promise<Regiment> {
    let report: AssessmentReport | null;

    if (this.activeTestProfile) {
      // Use test profile data
      console.log('RegimentService: Using TEST PROFILE:', this.activeTestProfile.name);
      report = this.createMockReport(this.activeTestProfile);
    } else {
      // Use real assessment data
      console.log('RegimentService: Using REAL assessment data');
      report = await this.getLatestAssessmentReport();
    }

    const overallAverage = this.calculateOverallAverage(report);
    const assessmentLevel = this.determineAssessmentLevel(overallAverage);
    const adjustment = this.getAdjustmentForLevel(assessmentLevel);
    const trainingRecommendations = this.generateTrainingRecommendations(adjustment);
    const subscoreFlags = this.identifyFocusAreas(report, overallAverage);

    console.log('RegimentService: Generated regiment with:', {
      overallAverage,
      assessmentLevel,
      adjustment,
      focusAreas: subscoreFlags.map(f => f.category)
    });

    const regiment: Regiment = {
      id: this.afs.createId(),
      userId: this.user.id,
      weekStartDate: this.getWeekStartDate().toISOString(),
      trainingRecommendations,
      overallAssessmentLevel: assessmentLevel,
      overallAverageScore: overallAverage,
      subscoreFlags,
      generatedAt: new Date().toISOString()
    };

    return regiment;
  }

  /**
   * Create a mock assessment report from a test profile
   */
  private createMockReport(profile: TestProfile): AssessmentReport {
    return {
      overall: (profile.scores.focus + profile.scores.speed + profile.scores.memory + profile.scores.strategy) / 4,
      focus: { number: profile.scores.focus, display: profile.scores.focus.toString() },
      speed: { number: profile.scores.speed, display: profile.scores.speed.toString() },
      memory: { number: profile.scores.memory, display: profile.scores.memory.toString() },
      strategy: { number: profile.scores.strategy, display: profile.scores.strategy.toString() },
      wisdom: { number: profile.scores.wisdom, display: profile.scores.wisdom.toString() },
      date: new Date().toISOString()
    };
  }

  /**
   * Generate and save a new regiment
   */
  private async generateAndSaveRegiment(): Promise<Regiment> {
    const regiment = await this.generateRegiment();

    // Remove any existing regiment for this week
    const weekStart = this.getWeekStartDate();
    const existingIndex = this.regiments.findIndex(r => {
      const rWeekStart = moment(r.weekStartDate).startOf('week');
      return rWeekStart.isSame(weekStart, 'day');
    });

    if (existingIndex >= 0) {
      // Delete old regiment from Firestore
      const oldRegiment = this.regiments[existingIndex];
      if (oldRegiment.id) {
        await this.deleteRegiment(oldRegiment.id);
      }
      this.regiments.splice(existingIndex, 1);
    }

    // Save new regiment
    await this.saveRegiment(regiment);
    this.regiments.push(regiment);

    this.currentRegiment = regiment;
    this.currentRegiment$.next(regiment);
    return regiment;
  }

  /**
   * Generate training recommendations with the given adjustment
   */
  private generateTrainingRecommendations(adjustment: number): TrainingRecommendation[] {
    const recommendations: TrainingRecommendation[] = [];

    for (const [trainingType, config] of Object.entries(this.BASE_TRAINING)) {
      const adjustedSessions = Math.max(1, config.sessions + adjustment);

      recommendations.push({
        trainingType: trainingType as TrainingType,
        displayName: config.displayName,
        sessionsPerWeek: adjustedSessions,
        baselineSessions: config.sessions,
        adjustment,
        iconPath: config.iconPath,
        sessionsCompleted: new Array(adjustedSessions).fill(false)
      });
    }

    return recommendations;
  }

  // ============================================
  // ASSESSMENT-DRIVEN ADJUSTMENTS
  // ============================================

  /**
   * Get the latest completed assessment report from the AssessmentService.
   * Uses the optimized getLatestCompletedReport method which only fetches
   * and calculates the most recent completed assessment.
   */
  private async getLatestAssessmentReport(): Promise<AssessmentReport | null> {
    try {
      console.log('RegimentService: Fetching latest assessment report for user:', this.user?.id);
      
      // Use the optimized method from AssessmentService
      const report = await this.assessmentService.getLatestCompletedReport(this.user.id);
      
      if (!report) {
        console.log('RegimentService: No completed assessment found for user - will use defaults');
        return null;
      }

      console.log('RegimentService: Got assessment report:', {
        overall: report.overall,
        focus: report.focus?.number ?? report.focus?.display,
        speed: report.speed?.number ?? report.speed?.display,
        memory: report.memory?.number ?? report.memory?.display,
        strategy: report.strategy?.number ?? report.strategy?.display
      });

      return report as AssessmentReport;
    } catch (error) {
      console.error('RegimentService: Error getting assessment report', error);
      return null;
    }
  }

  /**
   * Check if the user has any completed assessments
   */
  async hasCompletedAssessment(): Promise<boolean> {
    if (!this.user) { return false; }
    return await this.assessmentService.hasCompletedAssessment(this.user.id);
  }

  /**
   * Calculate overall average score excluding Wisdom.
   * Uses the 'number' property from assessment report (not 'display' which is a string).
   */
  private calculateOverallAverage(report: AssessmentReport | null): number {
    if (!report) {
      return 5;
    }

    // Extract numeric scores - use 'number' property, fallback to parsing 'display', then default to 5
    const getScore = (subscore: any): number => {
      if (!subscore) return 5;
      if (typeof subscore.number === 'number') return subscore.number;
      if (typeof subscore.display === 'string') return parseFloat(subscore.display) || 5;
      if (typeof subscore.display === 'number') return subscore.display;
      return 5;
    };

    const subscores = [
      getScore(report.focus),
      getScore(report.speed),
      getScore(report.memory),
      getScore(report.strategy)
    ];

    const sum = subscores.reduce((acc, score) => acc + score, 0);
    return sum / subscores.length;
  }

  /**
   * Determine the overall assessment level based on average score
   */
  private determineAssessmentLevel(averageScore: number): AssessmentLevel {
    if (averageScore >= this.SCORE_THRESHOLDS.HIGH) {
      return AssessmentLevel.High;
    } else if (averageScore <= this.SCORE_THRESHOLDS.LOW) {
      return AssessmentLevel.Low;
    }
    return AssessmentLevel.Average;
  }

  /**
   * Get the training adjustment for a given assessment level
   */
  private getAdjustmentForLevel(level: AssessmentLevel): number {
    switch (level) {
      case AssessmentLevel.High:
        return -1;
      case AssessmentLevel.Low:
        return 1;
      case AssessmentLevel.Average:
      default:
        return 0;
    }
  }

  /**
   * Identify subscores that are below the overall average.
   * Uses the 'number' property from assessment report (not 'display' which is a string).
   */
  private identifyFocusAreas(report: AssessmentReport | null, overallAverage: number): SubscoreFlag[] {
    if (!report) {
      return [];
    }

    // Helper to extract numeric score
    const getScore = (subscore: any): number => {
      if (!subscore) return 5;
      if (typeof subscore.number === 'number') return subscore.number;
      if (typeof subscore.display === 'string') return parseFloat(subscore.display) || 5;
      if (typeof subscore.display === 'number') return subscore.display;
      return 5;
    };

    const subscoreCategories: { category: SubscoreCategory; key: string }[] = [
      { category: SubscoreCategory.Focus, key: 'focus' },
      { category: SubscoreCategory.Speed, key: 'speed' },
      { category: SubscoreCategory.Memory, key: 'memory' },
      { category: SubscoreCategory.Strategy, key: 'strategy' }
    ];

    const flags: SubscoreFlag[] = [];

    for (const { category, key } of subscoreCategories) {
      const score = getScore(report[key]);
      const isBelowAverage = score < overallAverage;

      if (isBelowAverage) {
        flags.push({
          category,
          score,
          isBelowAverage: true
        });
      }
    }

    flags.sort((a, b) => a.score - b.score);
    return flags;
  }

  // ============================================
  // DATA PERSISTENCE (SUBCOLLECTION)
  // ============================================

  /**
   * Get the subcollection path for user regiments
   */
  private getRegimentsCollectionPath(): string {
    return `users/${this.user.id}/regiments`;
  }

  /**
   * Load user regiments from subcollection
   */
  private async loadUserRegiments(userId: string): Promise<void> {
    try {
      const collectionPath = `users/${userId}/regiments`;
      const snapshot = await this.afs.collection(collectionPath).ref.get();
      
      this.regiments = [];
      snapshot.forEach(doc => {
        const data = doc.data() as Regiment;
        data.id = doc.id;
        this.regiments.push(data);
      });

      // Sort by weekStartDate descending (most recent first)
      this.regiments.sort((a, b) => {
        return new Date(b.weekStartDate).getTime() - new Date(a.weekStartDate).getTime();
      });

      this.regimentsLoaded = true;
      this.regimentsLoaded$.next();
    } catch (error) {
      console.error('RegimentService: Error loading regiments', error);
      this.regiments = [];
      this.regimentsLoaded = true;
      this.regimentsLoaded$.next();
    }
  }

  /**
   * Save a regiment to the user's subcollection
   */
  private async saveRegiment(regiment: Regiment): Promise<void> {
    if (!this.user) { return; }

    const collectionPath = this.getRegimentsCollectionPath();
    const docPath = `${collectionPath}/${regiment.id}`;

    const saveData = {
      ...regiment,
      created_date: regiment.created_date || new Date().toISOString(),
      modified_date: new Date().toISOString()
    };

    try {
      await this.afs.doc(docPath).set(saveData);
    } catch (error) {
      console.error('RegimentService: Error saving regiment', error);
      throw error;
    }
  }

  /**
   * Delete a regiment from the user's subcollection
   */
  private async deleteRegiment(regimentId: string): Promise<void> {
    if (!this.user) { return; }

    const collectionPath = this.getRegimentsCollectionPath();
    const docPath = `${collectionPath}/${regimentId}`;

    try {
      await this.afs.doc(docPath).delete();
    } catch (error) {
      console.error('RegimentService: Error deleting regiment', error);
    }
  }

  /**
   * Ensure regiments are loaded before operations
   */
  private async ensureRegimentsLoaded(): Promise<void> {
    if (this.regimentsLoaded) { return; }

    const maxWaitTime = 5000;
    const checkInterval = 100;
    let waited = 0;

    while (!this.regimentsLoaded && waited < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waited += checkInterval;
    }

    if (!this.regimentsLoaded) {
      throw new Error('RegimentService: Timed out waiting for regiments to load');
    }
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Get the start of the current week (Sunday)
   */
  private getWeekStartDate(): Date {
    return moment().startOf('week').toDate();
  }

  /**
   * Find a regiment for a specific week
   */
  private findRegimentForWeek(weekStart: Date): Regiment | null {
    return this.regiments.find(regiment => {
      const regimentWeekStart = moment(regiment.weekStartDate).startOf('week');
      return regimentWeekStart.isSame(moment(weekStart), 'day');
    }) || null;
  }

  // ============================================
  // UI HELPER METHODS
  // ============================================

  /**
   * Get a user-friendly description of the assessment level
   */
  getAssessmentLevelDescription(level: AssessmentLevel): string {
    switch (level) {
      case AssessmentLevel.High:
        return 'Your assessment scores are above average. Training frequency has been reduced to maintain your performance.';
      case AssessmentLevel.Low:
        return 'Your assessment scores indicate room for improvement. Training frequency has been increased to help you progress.';
      case AssessmentLevel.Average:
      default:
        return 'Your assessment scores are on target. Training frequency is at baseline levels.';
    }
  }

  /**
   * Get a user-friendly summary of focus areas
   */
  getFocusAreasSummary(flags: SubscoreFlag[]): string {
    if (!flags || flags.length === 0) {
      return 'No specific focus areas identified. Keep up the great work!';
    }

    const areas = flags.map(f => f.category).join(', ');
    return `Focus areas for this week: ${areas}`;
  }

  /**
   * Get completion percentage for a training recommendation
   */
  getCompletionPercentage(recommendation: TrainingRecommendation): number {
    if (!recommendation.sessionsCompleted || recommendation.sessionsCompleted.length === 0) {
      return 0;
    }
    const completed = recommendation.sessionsCompleted.filter(s => s).length;
    return Math.round((completed / recommendation.sessionsCompleted.length) * 100);
  }
}
